import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  FulfillmentGroupTagSource,
  Tag,
  TagKind,
} from "@redotech/redo-common-model-tags-common/tag";
import {
  AddTagActionStep,
  RemoveShipmentTagsActionStep,
} from "@redotech/redo-flows-common/oms/actions";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import {
  BadgePillThemeToColorMapping,
  BadgePillThemeToTypeMapping,
  TagPillThemes,
} from "@redotech/redo-web/tags/tag-pill";
import { memo, useMemo, useRef, useState } from "react";
import { useEventListener } from "usehooks-ts";
import { PartialWithRequiredType } from ".";

// Component for picking tag color
const TagColorPicker = memo(function TagColorPicker({
  tagName,
  onSave,
  onCancel,
}: {
  tagName: string;
  onSave: (theme: PillTheme) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const client = useRequiredContext(RedoMerchantRpcClientContext);
  const [selectedTheme, setSelectedTheme] = useState<PillTheme>(
    PillTheme.NORMAL,
  );

  const handleThemeClick = useHandler((theme: PillTheme) => {
    setSelectedTheme(theme);
  });

  const handleSave = useHandler(async () => {
    try {
      await client.upsertTag({
        tag: {
          name: tagName,
          kind: TagKind.FULFILLMENT_GROUP,
          pillTheme: selectedTheme,
          source: FulfillmentGroupTagSource.MERCHANT,
        },
      });
    } catch (error) {
      console.error("Failed to create tag:", error);
      toast(t("Failed to create tag. Please try again."), { variant: "error" });
      return;
    }
    onSave(selectedTheme);
  });

  return (
    <RedoModal
      onModalCloseRequested={onCancel}
      primaryButton={{ onClick: handleSave, text: t("Create tag") }}
      secondaryButton={{ onClick: onCancel, text: t("Cancel") }}
      title={t("Choose tag color")}
    >
      <Flex align="center" dir="row" gap="md" wrap="wrap">
        {TagPillThemes.map((theme) => (
          <div
            key={theme}
            onClick={() => handleThemeClick(theme)}
            style={{ cursor: "pointer" }}
            tabIndex={0}
          >
            <RedoBadge
              color={BadgePillThemeToColorMapping[theme]}
              size={selectedTheme === theme ? "md" : "xs"}
              text={tagName}
              type={BadgePillThemeToTypeMapping[theme]}
            />
          </div>
        ))}
      </Flex>
    </RedoModal>
  );
});

export const TagInput = memo(function TagInput({
  step,
  setStep,
  allowCreatingTags = true,
}: {
  step: PartialWithRequiredType<
    AddTagActionStep | RemoveShipmentTagsActionStep
  >;
  setStep: (
    step: PartialWithRequiredType<
      AddTagActionStep | RemoveShipmentTagsActionStep
    >,
  ) => void;
  allowCreatingTags?: boolean;
}) {
  const { t } = useTranslation();
  const client = useRequiredContext(RedoMerchantRpcClientContext);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [input, setInput] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [pendingTagName, setPendingTagName] = useState("");
  const [tagReload, setTagReload] = useState(Symbol());

  const tagsLoad = useLoad(
    async (signal) =>
      client.getTeamTags({ kind: TagKind.FULFILLMENT_GROUP }, { signal }),
    [tagReload],
  );

  useEventListener(
    "focus",
    (e) => {
      setOpen(true);
    },
    inputRef,
  );

  useEventListener(
    "blur",
    (e) => {
      // Use a small delay to allow click events to process first
      setTimeout(() => {
        setOpen(false);
      }, 150);
    },
    inputRef,
  );

  const addNewTag = useHandler((name: string, theme: PillTheme) => {
    const newTag: Tag = {
      name,
      pillTheme: theme,
      source: FulfillmentGroupTagSource.MERCHANT,
      kind: TagKind.FULFILLMENT_GROUP,
    };

    setStep({ ...step, tags: [...(step.tags || []), newTag] });
    setInput(""); // Clear input
    setPendingTagName("");
    setTagReload(Symbol());

    // Restore focus to input after creating tag
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  });

  // Cancel tag creation
  const cancelNewTag = useHandler(() => {
    setPendingTagName("");

    // Restore focus to input
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  });

  const selectedTags = useMemo(
    () =>
      step.tags?.map((tag) => ({
        text: tag?.name || "",
        color: BadgePillThemeToColorMapping[tag?.pillTheme || PillTheme.NORMAL],
        type: BadgePillThemeToTypeMapping[tag?.pillTheme || PillTheme.NORMAL],
      })) ?? [],
    [step.tags],
  );

  const availableTags: RedoListItem<Tag | null>[] = useMemo(() => {
    return (
      tagsLoad.value?.tags.map((tag) => {
        return {
          id: tag.name,
          type: "badge" as const,
          badge: {
            text: tag.name,
            color: BadgePillThemeToColorMapping[tag.pillTheme],
            type: BadgePillThemeToTypeMapping[tag.pillTheme],
          },
          value: tag,
        };
      }) || []
    );
  }, [tagsLoad.value?.tags]);

  const filteredTags = useMemo(() => {
    const trimmedInput = input.trim().toLowerCase();

    // Filter available tags by search input and exclude already selected tags
    const tags = availableTags.filter((tag) => {
      return (
        !step.tags?.some(
          (selectedTag) => selectedTag?.name === tag.value?.name,
        ) && tag.value?.name.toLowerCase().includes(trimmedInput)
      );
    });

    const result = [...tags];

    // Only show "Create new tag" option if there's input text and no exact match
    if (
      allowCreatingTags &&
      trimmedInput &&
      !tags.some((tag) => tag.value?.name.toLowerCase() === trimmedInput)
    ) {
      result.unshift({
        id: `new-tag-${trimmedInput}`,
        type: "text" as const,
        text: t('Create tag "{{tagName}}"', { tagName: input.trim() }),
        value: null,
      });
    }

    // If no results and no input, show a helpful message
    if (result.length === 0) {
      if (!trimmedInput) {
        return [
          {
            id: "empty-state",
            type: "text" as const,
            text: allowCreatingTags
              ? t("Type to search or create tags")
              : t("Type to search tags"),
            value: null,
          },
        ];
      } else {
        return allowCreatingTags
          ? [
              {
                id: `new-tag-${trimmedInput}`,
                type: "text" as const,
                text: t('Create tag "{{tagName}}"', { tagName: input.trim() }),
                value: null,
              },
            ]
          : [];
      }
    }

    return result;
  }, [allowCreatingTags, availableTags, step.tags, input, t]);

  const handleSelectItem = useHandler(
    async ({ item }: { item: RedoListItem<Tag | null> }) => {
      if (item.value) {
        // If this is an existing tag, add it directly
        const isDuplicate = step.tags?.some(
          (tag) => tag?.name === item.value?.name,
        );

        if (!isDuplicate) {
          setStep({ ...step, tags: [...(step.tags || []), item.value] });
        }
        if (inputRef.current) {
          setInput("");
          inputRef.current.value = "";
          inputRef.current.focus();
          setOpen(true);
        }
      } else if (
        allowCreatingTags &&
        item.value === null &&
        item.id !== "empty-state"
      ) {
        // If this is the "Create tag" option, show the color picker
        if (input.trim()) {
          const trimmedInput = input.trim();
          // Check for duplicate before showing color picker
          const isDuplicate = step.tags?.some(
            (tag) => tag?.name === trimmedInput,
          );

          if (!isDuplicate) {
            setPendingTagName(trimmedInput);
            setOpen(false);
          }
        }
      } else {
        // This is the empty state item, just keep focus
        inputRef.current?.focus();
      }
    },
  );

  const handleSetTags = useHandler((tags: string[]) => {
    // Initialize tags to the original list, with any missing ones removed
    const newTags: Tag[] =
      (step.tags?.filter(
        (tag) => !!tag?.name && tags.includes(tag.name),
      ) as Tag[]) || [];

    // Find tags that need to be added (new tags)
    const tagToAdd = tags.find(
      (tag) => !step.tags?.some((t) => t?.name === tag),
    );

    if (tagToAdd) {
      const match = availableTags.find((tag) => tag.value?.name === tagToAdd);
      if (match) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        newTags.push(match.value!);
      } else if (allowCreatingTags) {
        setPendingTagName(tagToAdd);
        setOpen(false);
      }
    }
    setStep({ ...step, tags: newTags });
  });

  return (
    <>
      <div ref={anchorRef}>
        <RedoTagInputField
          inputValue={input}
          ref={inputRef}
          setInputValue={setInput}
          setTags={handleSetTags}
          tags={selectedTags}
        />
      </div>
      <div onKeyDown={(e) => e.preventDefault()}>
        <RedoListDropdown<Tag | null>
          dropdownAnchor={anchorRef.current}
          dropdownOpen={open}
          fitToAnchor
          items={filteredTags}
          itemSelected={handleSelectItem}
          // This needs to be a noop because the list does not take focus, the input field does.
          setDropdownOpen={() => {}}
        />
      </div>

      {pendingTagName ? (
        <TagColorPicker
          onCancel={cancelNewTag}
          onSave={(theme) => addNewTag(pendingTagName, theme)}
          tagName={pendingTagName}
        />
      ) : null}
    </>
  );
});
